import { useMemo, useState } from 'react';
import { useMarketData } from '../../hooks/useMarketData';
import { useMarketDataContext } from '../../context/MarketDataContext.utils';
import type { Vendor, Category } from '../../types';
import { VendorRow } from '../../features/vendor/components/VendorRow';
import { SearchIcon } from '../primitives/Icon';
import { Scrollable } from '../primitives/Scrollable';
import { sortKey } from '../../utils/sortKey';
import styles from './ListView.module.css';

interface Props {
  onTap: (vendor: Vendor) => void;
  selectedVendorUid?: string | undefined;
}

type Sort = 'alpha' | 'category';

interface CategoryGroup {
  category: Category;
  vendors: Vendor[];
}

export function ListView({ onTap, selectedVendorUid }: Props) {
  const { filteredVendors, categories, nameFor, filters } = useMarketData();
  const { setSearch, showAllCategories } = useMarketDataContext();
  const [sort, setSort] = useState<Sort>('alpha');

  const hasSearch = filters.search.trim().length > 0;
  const hasCategoryFilter =
    filters.categoryIds.size > 0 && filters.categoryIds.size < categories.length;

  const handleClearFilters = () => {
    if (hasSearch) setSearch('');
    if (hasCategoryFilter) showAllCategories();
  };

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // Vendors fan out across multiple booths in `filteredVendors` — one record
  // per Position they occupy. The map renders all of them as separate
  // markers; the list shows one row per vendor identity.
  const uniqueVendors = useMemo(() => {
    const seen = new Set<string>();
    const out: Vendor[] = [];
    for (const v of filteredVendors) {
      if (seen.has(v.uid)) continue;
      seen.add(v.uid);
      out.push(v);
    }
    return out;
  }, [filteredVendors]);

  // Header count is the vendor headcount only — excludes amenities. The
  // list below still shows all entries; this number answers "how many
  // vendors are here today?" in the strict farmers-market sense.
  const vendorCount = useMemo(
    () => uniqueVendors.filter((v) => v.type === 'vendor').length,
    [uniqueVendors],
  );

  const alphaSorted = useMemo(
    () => [...uniqueVendors].sort((a, b) => sortKey(a.name).localeCompare(sortKey(b.name))),
    [uniqueVendors],
  );

  const categoryGroups = useMemo<CategoryGroup[]>(() => {
    const groups = new Map<string, Vendor[]>();
    for (const v of uniqueVendors) {
      const arr = groups.get(v.categoryId) ?? [];
      arr.push(v);
      groups.set(v.categoryId, arr);
    }
    const out: CategoryGroup[] = [];
    for (const cat of categories) {
      const vendors = groups.get(cat.id);
      if (!vendors || vendors.length === 0) continue;
      vendors.sort((a, b) => sortKey(a.name).localeCompare(sortKey(b.name)));
      out.push({ category: cat, vendors });
    }
    return out.sort((a, b) => sortKey(a.category.name).localeCompare(sortKey(b.category.name)));
  }, [uniqueVendors, categories]);

  return (
    <div className={styles.listView}>
      <ListHeader sort={sort} onSortChange={setSort} count={vendorCount} />
      <Scrollable className={styles.scroll}>
        {uniqueVendors.length === 0 ? (
          <EmptyResults
            searchTerm={filters.search.trim()}
            hasCategoryFilter={hasCategoryFilter}
            onClear={handleClearFilters}
          />
        ) : sort === 'alpha' ? (
          <ul className={styles.list} aria-label={`Market list, ${alphaSorted.length} entries`}>
            {alphaSorted.map((v) => (
              <li key={v.uid} className={styles.listItem}>
                <VendorRow
                  vendor={v}
                  category={categoryById.get(v.categoryId)}
                  locationName={nameFor(v.positionId)}
                  isSelected={v.uid === selectedVendorUid}
                  onTap={onTap}
                />
              </li>
            ))}
          </ul>
        ) : (
          categoryGroups.map((group) => (
            <section
              key={group.category.id}
              aria-label={`${group.category.name}, ${group.vendors.length} entries`}
            >
              <header className={styles.groupHeader} data-cat-id={group.category.id}>
                <span className={styles.groupName}>{group.category.name}</span>
                <span className={styles.groupCount}>{group.vendors.length}</span>
              </header>
              <ul className={styles.list}>
                {group.vendors.map((v) => (
                  <li key={v.uid} className={styles.listItem}>
                    <VendorRow
                      vendor={v}
                      category={group.category}
                      locationName={nameFor(v.positionId)}
                      isSelected={v.uid === selectedVendorUid}
                      onTap={onTap}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </Scrollable>
    </div>
  );
}

function EmptyResults({
  searchTerm,
  hasCategoryFilter,
  onClear,
}: {
  searchTerm: string;
  hasCategoryFilter: boolean;
  onClear: () => void;
}) {
  const canClear = !!searchTerm || hasCategoryFilter;
  const title = searchTerm ? `No matches for “${searchTerm}”` : 'No vendors to show';
  return (
    <div className={styles.empty} role="status">
      <div className={styles.emptyIcon} aria-hidden="true">
        <SearchIcon size={28} color="var(--color-accent-default)" strokeWidth={1.8} />
      </div>
      <h3 className={styles.emptyTitle}>{title}</h3>
      <p className={styles.emptyBody}>
        Check for typos, or try a vendor name or category like <em>cheese</em>, <em>honey</em>, or{' '}
        <em>cider</em>.
      </p>
      {canClear && (
        <button type="button" className={styles.emptyAction} onClick={onClear}>
          Show all vendors
        </button>
      )}
    </div>
  );
}

function ListHeader({
  sort,
  onSortChange,
  count,
}: {
  sort: Sort;
  onSortChange: (s: Sort) => void;
  count: number;
}) {
  return (
    <div className={styles.header}>
      <div className={styles.count}>
        {count} Vendor{count === 1 ? '' : 's'}
      </div>
      <div className={styles.sortToggle} role="group" aria-label="Sort by">
        {(
          [
            ['alpha', 'A–Z'],
            ['category', 'Category'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`${styles.sortOption} ${sort === id ? styles.sortOptionOn : ''}`}
            onClick={() => onSortChange(id)}
            aria-pressed={sort === id}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
